import { useEffect, useMemo, useRef, useState } from 'react';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client.js';
import type { AccountNode, AccountResult } from '../../api/types.js';
import {
  scopeForNode,
  scopeFromParams,
  scopeToParams,
  usePins,
} from '../../state/accountScope.js';
import { queries } from '../../store/queries.js';
import { useQuery } from '../../store/StoreProvider.js';
import {
  accentFor,
  color,
  elevation,
  font,
  fontSize,
  isUiRegion,
  radius,
} from '../../theme/tokens.js';
import { AccountList } from './AccountList.js';
import { describeResult, hrefFor } from './navigate.js';

/**
 * The account palette (FR-049, FR-050, FR-051, FR-075, FR-076).
 *
 * Opens from anywhere with ⌘K, and from the Search Account control in Region Detail's header.
 * It has **two modes**, and which one you get depends on whether the address names a region:
 *
 * | Where it opens          | What it shows                                     | What a row does        |
 * | ----------------------- | ------------------------------------------------- | ---------------------- |
 * | `/regions/:code`        | that region's account list — MSPs, parents, children | scopes the chart in place |
 * | anywhere else           | the global prefix search                          | navigates to the region |
 *
 * Two modes rather than one because the two answer different questions. The list is a *browser*:
 * it opens populated, it is region-bound, and clicking a row changes what the page behind it is
 * drawing. The search is a *jump*: it is global, it needs a query before it can say anything,
 * and clicking a row leaves the page. Region Detail is the only page with the visible control
 * (owner decision 2026-08-28) precisely because it is the only page a scope means anything on.
 *
 * ⚠ **The global search still runs in region mode**, and its out-of-region hits are listed under
 * the table. Without it, ⌘K on a region page would silently lose the ability to reach a client
 * in another region — which is FR-049/FR-050 and is the whole reason the palette is global. The
 * section only appears once something outside the region matches, so it costs nothing to a
 * reader who is browsing.
 *
 * **An empty result is an empty state, never a blank panel** (FR-051), in both modes: a blank
 * panel is indistinguishable from a hung request.
 */

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 180;

/**
 * The first actionable row, in whichever mode is rendered.
 *
 * Found in the DOM rather than held in state because the two modes render two different
 * structures — a table of buttons, and a list of buttons — and the input above them is the one
 * control that has to drive either. The account row wins where both are present: in region mode
 * the search results are the *secondary* section, under the table.
 */
function firstRow(): HTMLButtonElement | null {
  return (
    document.querySelector<HTMLButtonElement>('[data-row-select]') ??
    document.querySelector<HTMLButtonElement>('[data-search-result]')
  );
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /**
   * The signed-in address, for the pin namespace.
   *
   * ⚠ Required rather than optional: pins live in `localStorage`, which outlives a session on a
   * shared machine, and an unnamespaced key would greet the next person with someone else's
   * book of accounts — the reason `dataStore` is namespaced too.
   */
  owner: string;
}

export function CommandPalette({ open, onClose, owner }: CommandPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  // The address, not a prop: the palette is mounted once in the shell and has no idea which page
  // is behind it. `useMatch` is how it finds out, and it is what makes the mode follow the route
  // rather than every page having to declare one.
  const regionMatch = useMatch('/regions/:regionCode');
  const routeRegion = regionMatch?.params.regionCode ?? '';
  const region = isUiRegion(routeRegion) ? routeRegion : null;

  // ⌘K / Ctrl-K from anywhere, and Escape to dismiss.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        if (!open) window.dispatchEvent(new CustomEvent('bob:open-palette'));
      }
      if (event.key === 'Escape' && open) onClose();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    // Focus lands in the input so the shortcut is usable without touching the mouse.
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const search = useAccountSearch(open ? query : '');

  if (!open) return null;

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: color.scrim,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '12vh',
        zIndex: 100,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={region ? `Accounts in ${region}` : 'Search accounts'}
        onClick={(event) => event.stopPropagation()}
        style={{
          // Wider in region mode: three columns, and the Name column carries a two-line row.
          width: region ? 'min(760px, 94vw)' : 'min(620px, 92vw)',
          background: color.surface,
          border: `1px solid ${color.borderStrong}`,
          borderRadius: radius['3xl'],
          boxShadow: elevation.palette,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '76vh',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            padding: '13px 16px',
            borderBottom: `1px solid ${color.borderSubtle}`,
            flex: 'none',
          }}
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke={color.inkFaint}
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3-3" />
          </svg>

          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              /*
               * ArrowDown hands focus to the list rather than moving an
               * `aria-activedescendant`: in region mode the rows are real buttons, which is what
               * lets each one carry a pin control. See `AccountList`.
               *
               * Enter activates the first row without leaving the input — type, Enter, done. It
               * is here because it is the flow the palette existed for, and losing it would be
               * a silent regression for anyone who never touches the mouse.
               */
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                firstRow()?.focus();
              } else if (event.key === 'Enter') {
                event.preventDefault();
                firstRow()?.click();
              }
            }}
            // Names all three levels, because all three are in the list and a reader who only
            // knows the facility's name would otherwise assume it is not searchable.
            placeholder="Type parent client / MSP / client name…"
            aria-label="Type a parent client, MSP or child client name to search accounts"
            style={{
              border: 'none',
              outline: 'none',
              background: 'none',
              fontFamily: 'inherit',
              fontSize: fontSize.sectionTitle,
              width: '100%',
              color: color.ink,
            }}
          />

          <span
            className="mono"
            style={{
              fontFamily: font.mono,
              fontSize: fontSize.caption,
              fontWeight: 600,
              color: color.inkSubtle,
              background: color.surfaceChip,
              border: `1px solid ${color.borderChip}`,
              borderRadius: radius.md,
              padding: '3px 8px',
            }}
          >
            esc
          </span>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {region ? (
            <RegionAccountsPanel
              region={region}
              owner={owner}
              query={query}
              onClose={onClose}
              onFocusInput={() => inputRef.current?.focus()}
            />
          ) : null}

          {/* In region mode this is the FR-050 escape hatch and is headed accordingly; in global
              mode it is the whole panel. One renderer either way, so the two cannot drift. */}
          <SearchResults
            search={search}
            query={query}
            excludeRegion={region}
            headed={region !== null}
            onClose={onClose}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * The region's account list, and the click that scopes the chart.
 *
 * A separate component because `useQuery` is a hook and the region may be absent: the check has
 * to happen before the read, and a hook cannot sit behind a condition. The same reason
 * `RegionDetail` is two components.
 */
function RegionAccountsPanel({
  region,
  owner,
  query,
  onClose,
  onFocusInput,
}: {
  region: string;
  owner: string;
  query: string;
  onClose: () => void;
  onFocusInput: () => void;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accounts = useQuery(queries.regionAccounts(region));
  const pins = usePins(owner, region);

  const scope = useMemo(() => scopeFromParams(searchParams), [searchParams]);
  const nodes: AccountNode[] = accounts.data?.data?.nodes ?? [];

  /**
   * Selecting a node rewrites the address, and Region Detail reads the address.
   *
   * ⚠ **`replace: true`, and the URL rather than component state.** The scope has to survive a
   * reload and be shareable — the same requirement `?client=` was built for (FR-050) — and it
   * has to have exactly one source of truth, or a row selected here and a row clicked in the
   * breakdown would be two selections that disagree. `replace` because rescoping a chart is not
   * a navigation: pushing would make Back walk through every account a reader tried.
   *
   * Clicking the node that is already scoped clears it, so the same row is the way back to the
   * whole region — the behaviour the breakdown's own rows already have.
   */
  const select = (node: AccountNode) => {
    const next = scopeForNode(node);
    const isCurrent = scope?.scopeType === next.scopeType && scope.scopeKey === next.scopeKey;
    const params = scopeToParams(
      searchParams,
      isCurrent
        ? { scope: null }
        : // The node's own `clientKey` — its parent's for a child, `null` for an MSP. It is
          // resolved here, where the node is in hand, so the page never has to fetch the
          // account list just to know which breakdown row a facility belongs to.
          { scope: next, highlightClientKey: node.clientKey },
    );

    navigate({ pathname: `/regions/${region}`, search: params.toString() }, { replace: true });
    onClose();
  };

  if (accounts.error) {
    return (
      <div style={{ padding: '22px 18px', color: color.negative, fontSize: fontSize.bodyStrong }}>
        The account list for {region} could not be loaded. {accounts.error.message}
      </div>
    );
  }

  if (accounts.loading) {
    return (
      <div style={{ padding: '22px 18px', color: color.inkSubtle, fontSize: fontSize.bodyStrong }}>
        Loading {region}’s accounts…
      </div>
    );
  }

  return (
    <AccountList
      nodes={nodes}
      query={query}
      pins={pins}
      scope={scope}
      onSelect={select}
      onFocusInput={onFocusInput}
    />
  );
}

interface SearchState {
  results: AccountResult[];
  loading: boolean;
  error: string | null;
}

/**
 * The global prefix search — `/api/accounts/search`, debounced.
 *
 * ⚠ **Not routed through the client store, deliberately.** Its cache key is whatever someone is
 * typing, so the key space is unbounded and the hit rate is poor — the same reason the server
 * skips Redis for this resource. `store/queries.ts` names every request that *is* cached, and
 * this one's absence from it is the statement.
 */
function useAccountSearch(query: string): SearchState {
  const [state, setState] = useState<SearchState>({
    results: [],
    loading: false,
    error: null,
  });

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setState({ results: [], loading: false, error: null });
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));

    const timer = window.setTimeout(() => {
      api
        .searchAccounts(trimmed)
        .then((envelope) => {
          if (cancelled) return;
          setState({ results: envelope.data?.results ?? [], loading: false, error: null });
        })
        .catch(() => {
          if (cancelled) return;
          setState({
            results: [],
            loading: false,
            error: 'Account search is unavailable right now.',
          });
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  return state;
}

/**
 * The prefix-search results.
 *
 * `headed` is the region-mode rendering: a labelled section under the account table, holding
 * only the hits from *other* regions. `excludeRegion` is what removes the duplicates — every
 * in-region hit is already a row in the table above, and listing it twice would offer two
 * controls for one account that do two different things (scope the chart, versus navigate).
 */
function SearchResults({
  search,
  query,
  excludeRegion,
  headed,
  onClose,
}: {
  search: SearchState;
  query: string;
  excludeRegion: string | null;
  headed: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const hasQuery = query.trim().length >= MIN_QUERY_LENGTH;

  const results = excludeRegion
    ? search.results.filter((r) => r.regionCode !== excludeRegion)
    : search.results;

  const go = (result: AccountResult) => {
    navigate(hrefFor(result));
    onClose();
  };

  // In region mode the section is additive: it says nothing at all until there is something
  // outside the region to say. A heading over "Searching…" would flicker on every keystroke.
  if (headed && (!hasQuery || results.length === 0)) return null;

  if (!headed) {
    if (!hasQuery) {
      return (
        <div
          style={{ padding: '22px 18px', color: color.inkSubtle, fontSize: fontSize.bodyStrong }}
        >
          Type at least {MIN_QUERY_LENGTH} characters to search accounts.
        </div>
      );
    }
    if (search.loading) {
      return (
        <div
          style={{ padding: '22px 18px', color: color.inkSubtle, fontSize: fontSize.bodyStrong }}
        >
          Searching…
        </div>
      );
    }
    if (search.error) {
      return (
        <div style={{ padding: '22px 18px', color: color.negative, fontSize: fontSize.bodyStrong }}>
          {search.error}
        </div>
      );
    }
    if (results.length === 0) {
      // An empty state, never a blank panel (FR-051).
      return (
        <div style={{ padding: '22px 18px' }}>
          <div style={{ fontWeight: 600, fontSize: fontSize.bodyStrong, color: color.ink }}>
            No accounts match “{query.trim()}”
          </div>
          <div style={{ marginTop: 4, fontSize: fontSize.body, color: color.inkMuted }}>
            Try a shorter fragment of the client name, or the health-system name.
          </div>
        </div>
      );
    }
  }

  return (
    <div>
      {headed ? (
        <div
          style={{
            padding: '8px 16px',
            borderTop: `1px solid ${color.border}`,
            background: color.surfaceSubtle,
            fontSize: fontSize.tableHeaderWide,
            fontWeight: 600,
            letterSpacing: '.4px',
            textTransform: 'uppercase',
            color: color.inkSubtle,
          }}
        >
          Other regions
        </div>
      ) : null}

      {results.map((result) => (
        <button
          key={`${result.clientKey}-${result.regionCode}`}
          type="button"
          data-search-result
          onClick={() => go(result)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            padding: '10px 16px',
            border: 'none',
            borderBottom: `1px solid ${color.borderSubtle}`,
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: 'inherit',
            background: 'transparent',
          }}
        >
          <span
            className="mono"
            aria-hidden="true"
            style={{
              fontFamily: font.mono,
              fontSize: fontSize.micro,
              fontWeight: 600,
              color: color.onAccent,
              background: accentFor(result.regionCode),
              padding: '2px 6px',
              borderRadius: radius.sm,
              flex: 'none',
            }}
          >
            {result.regionCode}
          </span>

          <span style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                display: 'block',
                fontSize: fontSize.navItem,
                fontWeight: 600,
                color: color.ink,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {result.clientName}
            </span>
            <span
              style={{ display: 'block', fontSize: fontSize.captionStrong, color: color.inkSubtle }}
            >
              {result.regionName}
              {result.healthSystem ? ` · ${result.healthSystem}` : ''}
            </span>
          </span>

          <span className="sr-only">{describeResult(result)}</span>
        </button>
      ))}
    </div>
  );
}
